import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Layout from '../components/Layout';
import { api } from '../lib/api';
import { Button } from '../components/ui';
import DuplicatePropertyDialog from '../components/DuplicatePropertyDialog';
import {
  duplicateCandidatesFrom,
  friendlyErrorMessage,
  propertyIdFrom,
  reusableDocumentId,
  pendingUploadNotice,
  DuplicateCandidate,
  DuplicateAction,
  PendingUpload,
  createSubmitLock,
} from '../utils/duplicateChoice';

const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB
const MAX_FILE_SIZE_DISPLAY = '50MB';

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
}

type FileReadState = 'idle' | 'reading' | 'ready';

/**
 * `PendingUpload` plus the one thing only this page knows: whether the Asset Management import
 * already ran for this document. Without it, a resubmit that reuses the document would either
 * skip an import the user has since switched on, or run it a second time and double up the
 * tenants and expenses.
 */
type PendingDocument = PendingUpload & { assetsImported: boolean };

export default function UploadDocument() {
  const [file, setFile] = useState<File | null>(null);
  const [fileBuffer, setFileBuffer] = useState<ArrayBuffer | null>(null);
  const [fileReadState, setFileReadState] = useState<FileReadState>('idle');
  const [documentType, setDocumentType] = useState('offering_memorandum');
  const [propertyId, setPropertyId] = useState<string>('');
  const [properties, setProperties] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [dragActive, setDragActive] = useState(false);
  const [importToAssetMgmt, setImportToAssetMgmt] = useState(false);
  // A5. The document is already uploaded and extracted by the time the duplicate check runs,
  // so its id has to be held here - "Attach here" needs it, and so does a retry with
  // force_create. Without it the answer to the prompt would have nothing to act on.
  //
  // It also SURVIVES Cancel, on purpose. Cancelling used to drop it, which made the obvious
  // next move (press Submit again) re-upload the same PDF and re-run Claude extraction on it -
  // real money on a 100-page OM - only to hit the identical 409 and orphan a second
  // `documents` row. Keeping it is what lets a resubmit reuse the extraction already paid for.
  const [duplicates, setDuplicates] = useState<DuplicateCandidate[] | null>(null);
  const [pendingUpload, setPendingUpload] = useState<PendingDocument | null>(null);
  // Not an error: the upload succeeded. Rendered in its own banner so it does not read as a
  // failure the user has to undo.
  const [notice, setNotice] = useState('');
  // NN0: the deal this upload is FOR; the new property goes back to it.
  const [returnDealId, setReturnDealId] = useState<string | null>(null);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const fileInputRef = useRef<HTMLInputElement>(null);
  // A5. Single-flight lock for the duplicate dialog's actions, acquired synchronously.
  // `busy` state cannot do this job: React batches updates, so two clicks in the same tick
  // both see it as false. See createSubmitLock's comment - this created two real rows.
  const submitLock = useRef(createSubmitLock());

  useEffect(() => {
    const loadProperties = async () => {
      try {
        const result: any = await api.listProperties();
        setProperties(result.properties || []);
      } catch (err) {
        console.error('Failed to load properties:', err);
      }
    };
    loadProperties();

    const propId = searchParams.get('property_id');
    setReturnDealId(searchParams.get('deal'));
    if (propId) {
      setPropertyId(propId);
      setDocumentType('comp');
    }
  }, [searchParams]);

  const documentTypes = [
    { value: 'offering_memorandum', label: 'Offering Memorandum' },
    { value: 'property_profile', label: 'Property Profile (CoStar, Crexi, etc.)' },
    { value: 'title_report', label: 'Title Report' },
    { value: 'comp', label: 'Comparable Sale' },
    { value: 'lease', label: 'Lease Agreement' },
    { value: 'appraisal', label: 'Appraisal' },
    { value: 'environmental_report', label: 'Environmental Report' },
    { value: 'other', label: 'Other' },
  ];

  const processSelectedFile = async (selectedFile: File) => {
    const isPdf = selectedFile.type === 'application/pdf' ||
      selectedFile.type === 'application/x-pdf' ||
      selectedFile.name.toLowerCase().endsWith('.pdf');
    if (!isPdf) {
      setError('Please upload a PDF file');
      return;
    }

    if (selectedFile.size > MAX_FILE_SIZE) {
      setError(`File too large (${formatFileSize(selectedFile.size)}). Maximum size is ${MAX_FILE_SIZE_DISPLAY}.`);
      return;
    }

    setFile(selectedFile);
    setFileBuffer(null);
    setError('');
    // The notice promises the next submit will not re-upload, which is only true for the file
    // it was written about. `pendingUpload` itself is kept - `reusableDocumentId` compares the
    // file, so picking the original one back up still skips the re-extraction.
    setNotice('');
    setFileReadState('reading');

    try {
      const buffer = await selectedFile.arrayBuffer();

      if (buffer.byteLength === 0) {
        setError('This file hasn\'t been downloaded yet. Right-click it in Finder and choose "Download Now" or "Make Available Offline", then try again.');
        setFile(null);
        setFileReadState('idle');
        return;
      }

      setFileBuffer(buffer);
      setFileReadState('ready');
    } catch (readErr) {
      console.error('File read failed:', readErr);
      setError('Could not read this file. If it\'s stored in iCloud, Dropbox, or Google Drive, make sure it\'s downloaded locally first.');
      setFile(null);
      setFileReadState('idle');
    }
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      processSelectedFile(e.dataTransfer.files[0]);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      processSelectedFile(e.target.files[0]);
    }
  };

  // All three property creates answer `{ success, message, property }` - NOT `{ data }`.
  // `propertyIdFrom` is the single tested reader of that shape, shared with Dashboard and
  // MasterPropertyDetail so the three pages cannot drift into reading it three ways. On an
  // unrecognised shape the property still EXISTS, so /dashboard is the honest landing spot -
  // never an error message.
  const goToCreated = async (result: unknown) => {
    const id = propertyIdFrom(result);
    if (id && returnDealId) {
      // NN0: the property was uploaded FOR a deal - link it and go back there. A failed link
      // still lands on the property (it exists) rather than on an error.
      try {
        await api.attachDealProperty(returnDealId, { property_id: id });
        navigate(`/crm/deals/${returnDealId}`);
        return;
      } catch (err) {
        console.error('Could not link the new property to the deal:', err);
      }
    }
    navigate(id ? `/properties/${id}` : '/dashboard');
  };

  /**
   * The answer to the duplicate prompt (A5).
   *
   * `create_separate` is the ONLY place force_create is ever passed. It is never a default,
   * never a retry-on-error, and never reachable from anything but this button - a duplicate
   * row has to be something the user asked for in as many words.
   */
  const handleDuplicateChoice = async (action: DuplicateAction, c: DuplicateCandidate) => {
    const documentId = pendingUpload?.documentId;
    if (!documentId) return;

    // ⚠️ Synchronous, before any await. `loading` is React state and is batched, so two
    // clicks in the same tick both pass it. On "Create separate" that means two properties.
    if (!submitLock.current.tryAcquire()) return;
    setLoading(true);
    setError('');
    setNotice('');
    try {
      if (action === 'attach' || action === 'open') {
        // The document is already uploaded and extracted; this just re-homes it onto the
        // property the user says it belongs to. Without it the upload is orphaned.
        //
        // `open` no longer reaches here - `availableActions` withholds it whenever there is a
        // documentId - but it is routed to attach rather than left to fall through, because
        // the two differ ONLY in whether the document survives. A bare navigate landed on the
        // same property and left the extracted `documents` row pointing at nothing, with no
        // documents list anywhere in the app to ever find it from again.
        await api.attachDocumentToProperty(c.id, documentId);
        navigate(`/properties/${c.id}`);
        return;
      }
      await goToCreated(await api.createPropertyFromDocument(documentId, undefined, true));
    } catch (err: any) {
      // Dismiss on failure rather than leaving a dialog whose buttons just failed. The error
      // lands in the form's existing banner, and `pendingUpload` is deliberately KEPT so the
      // notice below can still tell the user the document is uploaded and waiting.
      setDuplicates(null);
      setError(friendlyErrorMessage(err, 'Could not complete that action'));
      setNotice(pendingUploadNotice(pendingUpload) ?? '');
    } finally {
      submitLock.current.release();
      setLoading(false);
    }
  };

  /**
   * Cancel is NOT a no-op here, and dismissing the dialog silently was the bug.
   *
   * At this point the file has been uploaded and extracted, and there is a `documents` row
   * attached to no property. The form behind the dialog looks completely untouched, so the
   * user's natural next move is to press Submit again - and before this, that re-uploaded the
   * file, re-ran Claude extraction on the same PDF, orphaned a SECOND row, and came back with
   * the identical 409. Saying what happened, and keeping `pendingUpload` so the resubmit is
   * free, is what makes that loop stop.
   */
  const handleDuplicateCancel = () => {
    setDuplicates(null);
    setNotice(pendingUploadNotice(pendingUpload) ?? '');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!file || !fileBuffer) {
      setError('Please select a file');
      return;
    }

    if (documentType === 'comp' && !propertyId) {
      setError('Please select a property for this comparable');
      return;
    }

    setLoading(true);
    setError('');
    setNotice('');

    try {
      // A resubmit after a cancelled duplicate prompt reuses the document already uploaded and
      // already extracted. Uploading and extracting again would cost a real Claude call on the
      // same PDF (a 100-page OM is not cheap), leave a second orphaned `documents` row behind,
      // and end at the exact same 409. `reusableDocumentId` fails closed: any change to the
      // file name, its size, or the document type and this is null, so a different PDF is
      // always uploaded fresh.
      const reusableId = reusableDocumentId(pendingUpload, {
        fileName: file.name,
        fileSize: file.size,
        documentType,
      });

      let documentId: string;
      // Carried across the reuse so the import below runs exactly once for a given document,
      // even if the user switches the toggle on between the two submits.
      let assetsImported = false;

      if (reusableId) {
        documentId = reusableId;
        assetsImported = pendingUpload?.assetsImported ?? false;
      } else {
        const result: any = await api.uploadDocument(
          file,
          documentType === 'comp' ? propertyId : undefined,
          documentType
        );
        documentId = result.document.id;

        // Extract data from document (writes to master_properties automatically)
        await api.extractDocument(documentId);
      }

      // Import tenants/expenses to Asset Management if toggled on
      if (importToAssetMgmt && !assetsImported) {
        try {
          await api.importAssetsFromDocument(documentId);
          assetsImported = true;
        } catch (importErr: any) {
          console.warn('[Upload] Asset import warning:', importErr.message);
          // Don't block navigation — extraction succeeded
        }
      }

      if (documentType === 'comp' && propertyId) {
        navigate(returnDealId ? `/crm/deals/${returnDealId}` : `/properties/${propertyId}`);
      } else {
        // Create property in My Deals (properties table) from the extracted data
        // The extraction already wrote to master_properties automatically
        try {
          const propResult: any = await api.createPropertyFromDocument(documentId);
          await goToCreated(propResult);
        } catch (err: any) {
          // A 409 here is not a failure - the server declined to insert a second row for a
          // building we already have and handed back the ones it found. Ask, do not guess.
          // Anything else (including the 503 that means the CHECK broke) stays an error:
          // offering "create separately" in response to an outage would be a duplicate
          // factory.
          const candidates = duplicateCandidatesFrom(err);
          if (candidates) {
            setDuplicates(candidates);
            setPendingUpload({
              documentId,
              fileName: file.name,
              fileSize: file.size,
              documentType,
              assetsImported,
            });
            return;
          }
          setError(friendlyErrorMessage(err, 'Failed to create property from document'));
        }
      }
    } catch (err: any) {
      console.error('[Upload] Error:', err);
      setError(friendlyErrorMessage(err, 'Upload failed'));
    } finally {
      setLoading(false);
    }
  };

  const canSubmit = file && fileReadState === 'ready';

  return (
    <Layout>
      <div className="max-w-2xl mx-auto space-y-6">
        <div>
          <h1 className="font-display text-[28px] font-semibold uppercase leading-[1.06] tracking-[-.01em] text-gray-900 sm:text-[32px]">Upload Document</h1>
          <p className="mt-2 text-sm text-gray-600">
            Upload a PDF document to automatically extract property data
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
              {error}
            </div>
          )}

          {/*
            Blue, not red: the upload worked. This is the state left behind when the duplicate
            prompt is cancelled, and its whole job is to stop the user assuming the form did
            nothing and paying for a second extraction.
          */}
          {notice && (
            <div className="bg-info-wash border border-info/30 text-info px-4 py-3 rounded text-sm">
              {notice}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Document Type
            </label>
            <select
              value={documentType}
              onChange={(e) => { setDocumentType(e.target.value); setNotice(''); }}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
            >
              {documentTypes.map((type) => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
          </div>

          {documentType === 'comp' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Property *
              </label>
              <select
                value={propertyId}
                onChange={(e) => setPropertyId(e.target.value)}
                required
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
              >
                <option value="">Choose a property...</option>
                {properties.map((prop) => (
                  <option key={prop.id} value={prop.id}>
                    {prop.address || 'Vacant Land'}, {prop.city} - {prop.property_type}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-sm text-gray-500">
                This comparable will be added to the selected property
              </p>
            </div>
          )}

          {/* Asset Management Import Toggle */}
          {(documentType === 'offering_memorandum' || documentType === 'property_profile') && (
            <div className="flex items-center gap-3">
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={importToAssetMgmt}
                  onChange={(e) => setImportToAssetMgmt(e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-primary-500"></div>
              </label>
              <div>
                <span className="text-sm font-medium text-gray-700">Import to Asset Management</span>
                <p className="text-xs text-gray-500">Also import tenants & expenses into managed property</p>
              </div>
            </div>
          )}

          {/* File Upload */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">PDF File</label>
            <div
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
              className={`mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-dashed rounded-md ${
                dragActive
                  ? 'border-primary-500 bg-primary-50'
                  : fileReadState === 'ready'
                  ? 'border-success bg-success-wash'
                  : 'border-gray-300 bg-white'
              } transition-colors`}
            >
              <div className="space-y-1 text-center">
                {fileReadState === 'reading' ? (
                  <>
                    <svg className="mx-auto h-10 w-10 text-primary-500 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    <p className="text-sm text-gray-600 mt-2">Reading file...</p>
                  </>
                ) : fileReadState === 'ready' && file ? (
                  <>
                    <svg className="mx-auto h-10 w-10 text-success" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <p className="text-sm text-gray-900 font-medium mt-2">
                      {file.name} ({formatFileSize(file.size)})
                    </p>
                    <button
                      type="button"
                      onClick={() => {
                        setFile(null);
                        setFileBuffer(null);
                        setFileReadState('idle');
                        setNotice('');
                        if (fileInputRef.current) fileInputRef.current.value = '';
                      }}
                      className="text-xs text-primary-500 hover:text-primary-600 mt-1"
                    >
                      Choose a different file
                    </button>
                  </>
                ) : (
                  <>
                    <svg
                      className="mx-auto h-12 w-12 text-gray-400"
                      stroke="currentColor"
                      fill="none"
                      viewBox="0 0 48 48"
                    >
                      <path
                        d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                        strokeWidth={2}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    <div className="flex text-sm text-gray-600">
                      <label className="relative cursor-pointer bg-white rounded-md font-medium text-primary-500 hover:text-primary-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-primary-500">
                        <span>Upload a file</span>
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept="application/pdf,.pdf"
                          onChange={handleFileChange}
                          className="sr-only"
                        />
                      </label>
                      <p className="pl-1">or drag and drop</p>
                    </div>
                    <p className="text-xs text-gray-500">PDF up to {MAX_FILE_SIZE_DISPLAY}</p>
                  </>
                )}
              </div>
            </div>
          </div>

          <div className="flex gap-3">
            <Button
              type="submit"
              disabled={!canSubmit || loading}
              isLoading={loading}
              className="flex-1"
              size="lg"
            >
              {loading ? 'Processing...' : 'Upload & Extract Data'}
            </Button>
            <Button
              type="button"
              variant="outline"
              size="lg"
              onClick={() => navigate('/dashboard')}
            >
              Cancel
            </Button>
          </div>
        </form>
      </div>

      {duplicates && (
        <DuplicatePropertyDialog
          candidates={duplicates}
          documentId={pendingUpload?.documentId}
          busy={loading}
          onChoose={handleDuplicateChoice}
          onCancel={handleDuplicateCancel}
        />
      )}
    </Layout>
  );
}
